import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { getUserWeeklyStreak, markWeeklyStreakSeen } from '../api/getClassesApi';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 16,
        padding: theme.spacing(1),
        maxWidth: 400,
    }
}));

const StreakContainer = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(2),
}));

const StreakNumber = styled(Typography)(({ theme }) => ({
    fontSize: '3rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    lineHeight: 1,
}));

const FireIcon = styled('div')({
    fontSize: '2rem',
    marginBottom: 8,
});

const WeeklyStreakNotification = ({ onNotificationSeen }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [streakData, setStreakData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        checkWeeklyStreak();
    }, []);

    const checkWeeklyStreak = async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await getUserWeeklyStreak();
            setStreakData(data);

            if (data.showNotification) {
                setOpen(true);
            } else {
                // No notification to show, immediately call the callback
                if (onNotificationSeen) {
                    onNotificationSeen();
                }
            }
        } catch (error) {
            console.error('Error checking weekly streak:', error);
            setError('Failed to load streak information');
            // Even on error, don't block the user
            if (onNotificationSeen) {
                onNotificationSeen();
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGotIt = async () => {
        try {
            await markWeeklyStreakSeen();
            setOpen(false);

            if (onNotificationSeen) {
                onNotificationSeen();
            }
        } catch (error) {
            console.error('Error marking streak notification as seen:', error);
            // Even on error, close the dialog and continue
            setOpen(false);
            if (onNotificationSeen) {
                onNotificationSeen();
            }
        }
    };

    const getNotificationContent = () => {
        if (!streakData) return null;

        const { notificationType, currentStreak, message } = streakData;

        switch (notificationType) {
            case 'streak_continued':
                return (
                    <StreakContainer>
                        <FireIcon>🔥</FireIcon>
                        <Typography variant="h5" gutterBottom>
                            Streak Continued!
                        </Typography>
                        <StreakNumber>{currentStreak}</StreakNumber>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            week{currentStreak !== 1 ? 's' : ''} in a row
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            {message}
                        </Typography>
                    </StreakContainer>
                );

            case 'streak_started':
                return (
                    <StreakContainer>
                        <FireIcon>🔥</FireIcon>
                        <Typography variant="h5" gutterBottom>
                            Streak Started!
                        </Typography>
                        <StreakNumber>1</StreakNumber>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            week completed
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            {message}
                        </Typography>
                    </StreakContainer>
                );

            case 'streak_broken':
                return (
                    <StreakContainer>
                        <FireIcon>💔</FireIcon>
                        <Typography variant="h5" gutterBottom>
                            Streak Broken
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            {message}
                        </Typography>
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                            <Typography variant="body2">
                                Don't worry! Everyone's streak resets sometimes. What matters is getting back on track.
                            </Typography>
                        </Box>
                    </StreakContainer>
                );

            case 'start_streak':
                return (
                    <StreakContainer>
                        <FireIcon>💪</FireIcon>
                        <Typography variant="h5" gutterBottom>
                            Start Your Streak!
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            {message}
                        </Typography>
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'white' }}>
                            <Typography variant="body2">
                                Weekly streaks track your consistency. Attend at least one class per week (Monday-Sunday) to build your streak!
                            </Typography>
                        </Box>
                    </StreakContainer>
                );

            default:
                return null;
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 9999
                }}
            >
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Dialog open={true} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Alert severity="error">
                        {error}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onNotificationSeen && onNotificationSeen()} variant="contained">
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <StyledDialog
            open={open}
            onClose={(event, reason) => {
                // Prevent closing by clicking outside or pressing escape
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
            }}
            maxWidth="sm"
            fullWidth
        >
            <DialogContent sx={{ p: 0 }}>
                {getNotificationContent()}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button
                    onClick={handleGotIt}
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ borderRadius: 25 }}
                >
                    Got it!
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default WeeklyStreakNotification;